import { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, Trash2, Search, Clock, AlertTriangle, Download, Paperclip, NotebookPen, MessageSquarePlus, PauseCircle, CalendarIcon, Pencil } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { format } from "date-fns";
import { toast } from "sonner";

const STATUS_LABEL = { new: "New", in_progress: "In Progress", on_hold: "On Hold", completed: "Completed", cancelled: "Cancelled" };
const STATUS_COLOR = {
  new: "bg-slate-100 text-slate-800 border-slate-200",
  in_progress: "bg-amber-100 text-amber-800 border-amber-200",
  on_hold: "bg-purple-100 text-purple-800 border-purple-200",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  cancelled: "bg-slate-100 text-slate-500 border-slate-200",
};
const PRIO_COLOR = {
  Low: "bg-slate-50 text-slate-700 border-slate-200",
  Medium: "bg-blue-50 text-blue-700 border-blue-200",
  High: "bg-orange-50 text-orange-700 border-orange-200",
  Urgent: "bg-red-50 text-red-700 border-red-200",
};
const PRIORITIES = ["Low", "Medium", "High", "Urgent"];

function PhotoPicker({ api, photos, setPhotos, testId }) {
  const [busy, setBusy] = useState(false);
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (photos.length + files.length > 5) { toast.error("Max 5 photos"); e.target.value = ""; return; }
    setBusy(true);
    try {
      const out = [...photos];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await axios.post(`${api}/uploads`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        out.push(r.data);
      }
      setPhotos(out);
      toast.success(`${out.length - photos.length} photo(s) attached`);
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <div>
      <label className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-slate-500 cursor-pointer border border-dashed border-slate-300 rounded-md px-3 py-2 hover:bg-slate-50" data-testid={`${testId}-label`}>
        <Paperclip className="w-4 h-4" />{busy ? "Uploading..." : "Attach photos (optional)"}
        <input type="file" accept="image/*" multiple hidden onChange={onFiles} data-testid={`${testId}-input`} />
      </label>
      {photos.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2" data-testid={`${testId}-list`}>
          {photos.map((p) => (
            <span key={p.path} className="text-xs font-mono bg-slate-100 border border-slate-200 rounded px-2 py-1 inline-flex items-center gap-1">
              <Paperclip className="w-3 h-3" />{p.path.split("/").pop().slice(0, 12)}…
              <button type="button" onClick={() => setPhotos(photos.filter((x) => x.path !== p.path))} className="text-slate-400 hover:text-red-600" data-testid={`${testId}-remove-${p.path.split("/").pop()}`}>×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function WorkOrderDialogs({
  api,
  completingOrder, setCompletingOrder, completionNotes, setCompletionNotes, confirmComplete,
  holdingOrder, setHoldingOrder, holdNotes, setHoldNotes, confirmHold,
  progressOrder, setProgressOrder, progressPhotos, setProgressPhotos, confirmProgress,
  updatingOrder, setUpdatingOrder, updateNote, setUpdateNote, updatePhotos, setUpdatePhotos, confirmUpdate,
  dueDateOrder, setDueDateOrder, dueDateValue, setDueDateValue, dueDateNa, setDueDateNa, confirmDueDate,
  editOrder, setEditOrder, editForm, setEditForm, confirmEdit, meta,
}) {
  return (
    <>
      <Dialog open={!!completingOrder} onOpenChange={(open) => { if (!open) setCompletingOrder(null); }}>
        <DialogContent className="bg-white" data-testid="completion-notes-dialog">
          <DialogHeader>
            <DialogTitle>Complete {completingOrder?.ticket_no}</DialogTitle>
            <DialogDescription>Mark this work order as completed.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-slate-600">Optionally leave notes about how this was resolved — they're shown on the dashboard and included in the email to the requester.</p>
            <Textarea
              placeholder="e.g. Replaced the faulty ballast and tested all three panels — lighting restored."
              value={completionNotes}
              onChange={(e) => setCompletionNotes(e.target.value)}
              className="bg-white min-h-[100px]"
              data-testid="completion-notes-textarea"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCompletingOrder(null)} data-testid="cancel-complete-button">Cancel</Button>
            <Button onClick={confirmComplete} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="confirm-complete-button">
              <CheckCircle2 className="w-4 h-4 mr-2" />Mark Complete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!holdingOrder} onOpenChange={(open) => { if (!open) setHoldingOrder(null); }}>
        <DialogContent className="bg-white" data-testid="hold-notes-dialog">
          <DialogHeader>
            <DialogTitle>Put On Hold — {holdingOrder?.ticket_no}</DialogTitle>
            <DialogDescription>Pause this work order with an explanatory comment.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-slate-600">Add a comment explaining why this is on hold — it's saved to the ticket and emailed to the requester.</p>
            <Textarea
              placeholder="e.g. Waiting on replacement part — ETA next Tuesday."
              value={holdNotes}
              onChange={(e) => setHoldNotes(e.target.value)}
              className="bg-white min-h-[100px]"
              data-testid="hold-notes-textarea"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setHoldingOrder(null)} data-testid="cancel-hold-button">Cancel</Button>
            <Button onClick={confirmHold} className="bg-purple-700 hover:bg-purple-800 text-white" data-testid="confirm-hold-button">
              <PauseCircle className="w-4 h-4 mr-2" />Put On Hold
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!progressOrder} onOpenChange={(open) => { if (!open) setProgressOrder(null); }}>
        <DialogContent className="bg-white" data-testid="progress-dialog">
          <DialogHeader>
            <DialogTitle>Mark In Progress — {progressOrder?.ticket_no}</DialogTitle>
            <DialogDescription>Start work on this ticket and optionally attach photos.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-slate-600">The requester will be emailed that work has started. Attach photos of the work area or parts if useful (optional).</p>
            <PhotoPicker api={api} photos={progressPhotos} setPhotos={setProgressPhotos} testId="progress-photos" />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setProgressOrder(null)} data-testid="cancel-progress-button">Cancel</Button>
            <Button onClick={confirmProgress} className="bg-[#478261] hover:bg-[#3a6b51] text-white" data-testid="confirm-progress-button">
              <Clock className="w-4 h-4 mr-2" />Mark In Progress
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editOrder} onOpenChange={(open) => { if (!open) setEditOrder(null); }}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-order-dialog">
          <DialogHeader>
            <DialogTitle>Edit Work Order — {editOrder?.ticket_no}</DialogTitle>
            <DialogDescription>Changes are logged in the ticket's update history. The requester is not emailed.</DialogDescription>
          </DialogHeader>
          {editForm && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-mono uppercase tracking-wider text-slate-500">Property</label>
                <Select value={editForm.property} onValueChange={(v) => setEditForm({ ...editForm, property: v })}>
                  <SelectTrigger className="bg-white" data-testid="edit-property-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{[...new Set([editForm.property, ...(meta?.properties || [])])].filter(Boolean).map((p) => <SelectItem key={p} value={p} data-testid={`edit-property-option-${p.replace(/\s+/g, "-")}`}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-mono uppercase tracking-wider text-slate-500">Issue Type</label>
                <Select value={editForm.issue_type} onValueChange={(v) => setEditForm({ ...editForm, issue_type: v })}>
                  <SelectTrigger className="bg-white" data-testid="edit-issue-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{[...new Set([editForm.issue_type, ...(meta?.issue_types || [])])].filter(Boolean).map((i) => <SelectItem key={i} value={i} data-testid={`edit-issue-option-${i.replace(/\s+/g, "-")}`}>{i}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-mono uppercase tracking-wider text-slate-500">Office / Area</label>
                <Input className="bg-white" value={editForm.office_area} onChange={(e) => setEditForm({ ...editForm, office_area: e.target.value })} data-testid="edit-office-area-input" />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-mono uppercase tracking-wider text-slate-500">Company Name</label>
                <Input className="bg-white" value={editForm.company_name} onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })} data-testid="edit-company-input" />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-mono uppercase tracking-wider text-slate-500">Priority</label>
                <Select value={editForm.priority} onValueChange={(v) => setEditForm({ ...editForm, priority: v })}>
                  <SelectTrigger className="bg-white" data-testid="edit-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{PRIORITIES.map((p) => <SelectItem key={p} value={p} data-testid={`edit-priority-option-${p}`}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-mono uppercase tracking-wider text-slate-500">Due Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" disabled={editForm.noDue} className="bg-white w-full justify-start font-normal" data-testid="edit-due-date-trigger">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {editForm.noDue ? "N/A" : (editForm.dueDate ? format(editForm.dueDate, "PPP") : "Pick a date")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white" align="start">
                    <Calendar mode="single" selected={editForm.dueDate} onSelect={(d) => setEditForm({ ...editForm, dueDate: d })} initialFocus disabled={editForm.noDue} />
                  </PopoverContent>
                </Popover>
                <div className="flex items-center gap-2 pt-1">
                  <Checkbox id="edit-order-no-due" checked={editForm.noDue} onCheckedChange={(v) => setEditForm({ ...editForm, noDue: !!v, dueDate: v ? null : editForm.dueDate })} data-testid="edit-no-due-checkbox" />
                  <label htmlFor="edit-order-no-due" className="text-sm text-slate-700 cursor-pointer select-none">No specific due date (N/A)</label>
                </div>
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <label className="text-xs font-mono uppercase tracking-wider text-slate-500">Description</label>
                <Textarea className="bg-white" rows={4} value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} data-testid="edit-description-textarea" />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditOrder(null)} data-testid="edit-cancel-button">Cancel</Button>
            <Button onClick={confirmEdit} className="bg-[#478261] hover:bg-[#3a6b51] text-white" data-testid="edit-save-button">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!dueDateOrder} onOpenChange={(open) => { if (!open) setDueDateOrder(null); }}>
        <DialogContent className="bg-white" data-testid="due-date-dialog">
          <DialogHeader>
            <DialogTitle>Change Due Date — {dueDateOrder?.ticket_no}</DialogTitle>
            <DialogDescription>Pick a new due date or mark as N/A. The change is logged in the ticket history.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <Popover>
              <PopoverTrigger asChild>
                <Button type="button" variant="outline" disabled={dueDateNa} className="bg-white justify-start font-normal" data-testid="due-date-edit-trigger">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dueDateNa ? "N/A" : (dueDateValue ? format(dueDateValue, "PPP") : "Pick a date")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-white" align="start">
                <Calendar mode="single" selected={dueDateValue} onSelect={setDueDateValue} initialFocus disabled={dueDateNa} />
              </PopoverContent>
            </Popover>
            <div className="flex items-center gap-2">
              <Checkbox id="edit-no-due" checked={dueDateNa} onCheckedChange={(v) => { setDueDateNa(!!v); if (v) setDueDateValue(null); }} data-testid="edit-na-due-date-checkbox" />
              <label htmlFor="edit-no-due" className="text-sm text-slate-700 cursor-pointer select-none">No specific due date (N/A)</label>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDueDateOrder(null)} data-testid="due-date-cancel-button">Cancel</Button>
            <Button onClick={confirmDueDate} className="bg-[#478261] hover:bg-[#3a6b51] text-white" data-testid="due-date-save-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!updatingOrder} onOpenChange={(open) => { if (!open) setUpdatingOrder(null); }}>
        <DialogContent className="bg-white" data-testid="update-note-dialog">
          <DialogHeader>
            <DialogTitle>Add Update — {updatingOrder?.ticket_no}</DialogTitle>
            <DialogDescription>Save a progress note and email the requester.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-slate-600">Add a progress update. It's saved to the ticket and emailed to the person who submitted the request.</p>
            <Textarea
              placeholder="e.g. Technician scheduled for Thursday 10 AM — parts on order."
              value={updateNote}
              onChange={(e) => setUpdateNote(e.target.value)}
              className="bg-white min-h-[100px]"
              data-testid="update-note-textarea"
            />
            <PhotoPicker api={api} photos={updatePhotos} setPhotos={setUpdatePhotos} testId="update-photos" />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setUpdatingOrder(null)} data-testid="cancel-update-button">Cancel</Button>
            <Button onClick={confirmUpdate} disabled={!updateNote.trim()} className="bg-[#478261] hover:bg-[#3a6b51] text-white" data-testid="confirm-update-button">
              <MessageSquarePlus className="w-4 h-4 mr-2" />Save Update &amp; Email Requester
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function WorkOrderList({ api, meta, refreshKey, onChanged }) {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("outstanding");
  const [property, setProperty] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [completingOrder, setCompletingOrder] = useState(null);
  const [completionNotes, setCompletionNotes] = useState("");
  const [holdingOrder, setHoldingOrder] = useState(null);
  const [holdNotes, setHoldNotes] = useState("");
  const [updatingOrder, setUpdatingOrder] = useState(null);
  const [updateNote, setUpdateNote] = useState("");
  const [updatePhotos, setUpdatePhotos] = useState([]);
  const [progressOrder, setProgressOrder] = useState(null);
  const [progressPhotos, setProgressPhotos] = useState([]);
  const [dueDateOrder, setDueDateOrder] = useState(null);
  const [dueDateValue, setDueDateValue] = useState(null);
  const [dueDateNa, setDueDateNa] = useState(false);
  const [editOrder, setEditOrder] = useState(null);
  const [editForm, setEditForm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (property !== "all") params.property = property;
      const res = await axios.get(`${api}/work-orders`, { params });
      setOrders(res.data);
    } catch (e) {
      toast.error("Failed to load work orders");
    } finally {
      setLoading(false);
    }
  }, [api, property]);
  useEffect(() => { load(); }, [refreshKey, load]);

  const today = new Date().toISOString().slice(0, 10);
  const filtered = useMemo(() => {
    let list = orders;
    if (filter === "outstanding") list = list.filter((o) => o.status === "new" || o.status === "in_progress" || o.status === "on_hold");
    else if (filter === "due") list = list.filter((o) => !o.no_due_date && o.due_date && o.due_date <= today && o.status !== "completed" && o.status !== "cancelled");
    else if (filter === "completed") list = list.filter((o) => o.status === "completed");
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter((o) => [o.ticket_no, o.company_name, o.office_area, o.description, o.issue_type].some((v) => (v || "").toLowerCase().includes(s)));
    }
    return list;
  }, [orders, filter, q, today]);

  const updateStatus = async (id, status, completedBy, notes, holdNotes) => {
    try {
      await axios.patch(`${api}/work-orders/${id}/status`, { status, completed_by: completedBy || null, completion_notes: notes || null, hold_notes: holdNotes || null });
      toast.success(status === "completed" ? "Marked as complete" : `Status: ${STATUS_LABEL[status]}`);
      load(); onChanged?.();
    } catch { toast.error("Failed to update status"); }
  };

  const requestComplete = (order) => { setCompletingOrder(order); setCompletionNotes(""); };

  const requestHold = (order) => { setHoldingOrder(order); setHoldNotes(""); };

  const confirmHold = async () => {
    if (!holdingOrder) return;
    await updateStatus(holdingOrder.id, "on_hold", null, null, holdNotes);
    setHoldingOrder(null);
    setHoldNotes("");
  };

  const confirmComplete = async () => {
    if (!completingOrder) return;
    await updateStatus(completingOrder.id, "completed", "Facilities", completionNotes);
    setCompletingOrder(null);
    setCompletionNotes("");
  };

  const attachPhotos = async (woId, photos) => {
    if (!photos.length) return;
    await axios.post(`${api}/work-orders/${woId}/photos`, { photos: photos.map((p) => p.path) });
  };

  const handleStatusSelect = (o, v) => {
    if (v === "completed") return requestComplete(o);
    if (v === "on_hold") return requestHold(o);
    if (v === "in_progress") return requestProgress(o);
    return updateStatus(o.id, v);
  };

  const requestProgress = (order) => { setProgressOrder(order); setProgressPhotos([]); };

  const confirmProgress = async () => {
    if (!progressOrder) return;
    await updateStatus(progressOrder.id, "in_progress");
    try {
      await attachPhotos(progressOrder.id, progressPhotos);
      if (progressPhotos.length) toast.success("Photos attached to ticket");
    } catch { toast.error("Photo attach failed"); }
    setProgressOrder(null);
    setProgressPhotos([]);
    load(); onChanged?.();
  };

  const requestUpdate = (order) => { setUpdatingOrder(order); setUpdateNote(""); setUpdatePhotos([]); };

  const confirmUpdate = async () => {
    if (!updatingOrder || !updateNote.trim()) return;
    try {
      await axios.post(`${api}/work-orders/${updatingOrder.id}/updates`, { note: updateNote });
      try { await attachPhotos(updatingOrder.id, updatePhotos); } catch { toast.error("Photo attach failed"); }
      toast.success("Update added — requester emailed");
      setUpdatingOrder(null);
      setUpdateNote("");
      setUpdatePhotos([]);
      load(); onChanged?.();
    } catch { toast.error("Failed to add update"); }
  };

  const requestDueDate = (order) => {
    setDueDateOrder(order);
    setDueDateNa(!!order.no_due_date);
    setDueDateValue(order.no_due_date || !order.due_date ? null : new Date(`${order.due_date}T00:00:00`));
  };

  const confirmDueDate = async () => {
    if (!dueDateOrder) return;
    if (!dueDateNa && !dueDateValue) { toast.error("Pick a date or choose N/A"); return; }
    try {
      await axios.patch(`${api}/work-orders/${dueDateOrder.id}/due-date`, {
        due_date: dueDateNa ? null : format(dueDateValue, "yyyy-MM-dd"),
        no_due_date: dueDateNa,
      });
      toast.success("Due date updated");
      setDueDateOrder(null);
      load(); onChanged?.();
    } catch { toast.error("Failed to update due date"); }
  };

  const requestEdit = (o) => {
    setEditOrder(o);
    setEditForm({
      property: o.property, issue_type: o.issue_type, office_area: o.office_area,
      company_name: o.company_name, priority: o.priority, description: o.description,
      dueDate: o.no_due_date || !o.due_date ? null : new Date(`${o.due_date}T00:00:00`),
      noDue: !!o.no_due_date,
    });
  };

  const confirmEdit = async () => {
    if (!editOrder || !editForm) return;
    if (!editForm.office_area.trim() || !editForm.company_name.trim() || !editForm.description.trim()) {
      toast.error("Office/Area, Company Name and Description are required"); return;
    }
    if (!editForm.noDue && !editForm.dueDate) { toast.error("Pick a due date or choose N/A"); return; }
    try {
      await axios.patch(`${api}/work-orders/${editOrder.id}`, {
        property: editForm.property, issue_type: editForm.issue_type,
        office_area: editForm.office_area, company_name: editForm.company_name,
        priority: editForm.priority, description: editForm.description,
        due_date: editForm.noDue ? null : format(editForm.dueDate, "yyyy-MM-dd"),
        no_due_date: editForm.noDue,
      });
      toast.success("Work order updated");
      setEditOrder(null); setEditForm(null);
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update work order"); }
  };

  const removeOrder = async (id) => {
    if (!window.confirm("Delete this work order?")) return;
    try { await axios.delete(`${api}/work-orders/${id}`); toast.success("Deleted"); load(); onChanged?.(); }
    catch { toast.error("Failed to delete"); }
  };

  const exportCsv = () => {
    const params = new URLSearchParams();
    if (property !== "all") params.set("property", property);
    params.set("status", filter);
    const url = `${api}/work-orders/export.csv?${params.toString()}`;
    window.open(url, "_blank");
    toast.success("CSV export started");
  };

  return (
    <div className="space-y-5">
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4 flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search ticket, company, area, description..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-white" data-testid="search-input" />
          </div>
          <Select value={property} onValueChange={setProperty}>
            <SelectTrigger className="md:w-[220px] bg-white" data-testid="filter-property-select"><SelectValue placeholder="All properties" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All properties</SelectItem>
              {meta.properties?.map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
            </SelectContent>
          </Select>
          <Tabs value={filter} onValueChange={setFilter}>
            <TabsList className="bg-slate-100 p-1" data-testid="filter-tabs">
              <TabsTrigger value="outstanding" data-testid="filter-outstanding" className="data-[state=active]:bg-[#478261] data-[state=active]:text-white">Outstanding</TabsTrigger>
              <TabsTrigger value="due" data-testid="filter-due" className="data-[state=active]:bg-[#478261] data-[state=active]:text-white">Due / Overdue</TabsTrigger>
              <TabsTrigger value="completed" data-testid="filter-completed" className="data-[state=active]:bg-[#478261] data-[state=active]:text-white">Completed</TabsTrigger>
              <TabsTrigger value="all" data-testid="filter-all" className="data-[state=active]:bg-[#478261] data-[state=active]:text-white">All</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button onClick={exportCsv} variant="outline" className="bg-white" data-testid="export-csv-button">
            <Download className="w-4 h-4 mr-2" />Export CSV
          </Button>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-3">
        {loading && <div className="text-sm text-slate-500 text-center py-6">Loading...</div>}
        {!loading && filtered.length === 0 && (
          <div className="text-center py-10 text-slate-500 bg-white border border-dashed border-slate-200 rounded-lg" data-testid="empty-state">No work orders match this view.</div>
        )}
        {filtered.map((o) => {
          const overdue = !o.no_due_date && o.due_date && o.due_date < today && o.status !== "completed";
          return (
            <Card key={o.id} className={o.is_project ? "border-2 border-red-500 shadow-sm hover:shadow-md transition-shadow" : "border-slate-200 shadow-sm hover:shadow-md transition-shadow"} data-testid={`work-order-card-${o.ticket_no}`}>
              <CardContent className="p-5">
                <div className="flex flex-col md:flex-row md:items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center flex-wrap gap-2 mb-2">
                      <span className="font-mono text-xs text-slate-500">{o.ticket_no}</span>
                      <Badge className={`border ${STATUS_COLOR[o.status]}`} data-testid={`status-badge-${o.ticket_no}`}>{STATUS_LABEL[o.status]}</Badge>
                      {o.is_project && <Badge className="border bg-red-600 text-white border-red-600" data-testid={`project-badge-${o.ticket_no}`}>PROJECT</Badge>}
                      <Badge className={`border ${PRIO_COLOR[o.priority]}`}>{o.priority}</Badge>
                      {overdue && <Badge className="border bg-red-600 text-white border-red-700"><AlertTriangle className="w-3 h-3 mr-1" />Overdue</Badge>}
                      {o.photos?.length > 0 && <Badge className="border bg-slate-50 text-slate-700 border-slate-200"><Paperclip className="w-3 h-3 mr-1" />{o.photos.length}</Badge>}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">
                      <span className="bg-slate-900 text-white px-2 py-0.5 rounded">{o.property}</span>
                      <span className="text-slate-400">·</span>
                      <span>{o.issue_type}</span>
                    </div>
                    <div className="text-sm text-slate-800"><strong>{o.office_area}</strong> — {o.company_name}</div>
                    <p className="text-sm text-slate-600 mt-1">{o.description}</p>
                    {o.photos?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {o.photos.map((path) => (
                          <a key={path} href={`/api/files/${path}`} target="_blank" rel="noreferrer" className="block w-16 h-16 rounded overflow-hidden border border-slate-200 bg-slate-100">
                            <img src={`/api/files/${path}`} alt="" className="w-full h-full object-cover" />
                          </a>
                        ))}
                      </div>
                    )}
                    <div className="mt-2 text-xs text-slate-500 flex flex-wrap gap-3 items-center">
                      <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />Due: <span className={overdue ? "text-red-600 font-semibold" : ""}>{o.no_due_date ? "N/A" : (o.due_date || "N/A")}</span></span>
                      {o.requester_name && <span>By: {o.requester_name}</span>}
                      {o.requester_email && <span className="text-slate-400">·</span>}
                      {o.requester_email && <span className="font-mono">{o.requester_email}</span>}
                      {o.completed_at && <span className="text-emerald-700">Completed {new Date(o.completed_at).toLocaleDateString()}{o.completed_by ? ` by ${o.completed_by}` : ""}</span>}
                    </div>
                    {o.completion_notes && (
                      <div className="mt-2 text-xs bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-emerald-900 inline-flex items-start gap-1.5" data-testid={`completion-notes-${o.ticket_no}`}>
                        <NotebookPen className="w-3.5 h-3.5 mt-0.5 shrink-0" />{o.completion_notes}
                      </div>
                    )}
                    {o.hold_notes && o.status === "on_hold" && (
                      <div className="mt-2 text-xs bg-purple-50 border border-purple-200 rounded-md px-3 py-2 text-purple-900 inline-flex items-start gap-1.5" data-testid={`hold-notes-${o.ticket_no}`}>
                        <PauseCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{o.hold_notes}
                      </div>
                    )}
                    {o.updates?.length > 0 && (
                      <div className="mt-2 space-y-1.5">
                        {o.updates.slice(-3).map((u, i) => (
                          <div key={`${u.at}-${i}`} className="text-xs bg-blue-50 border border-blue-200 rounded-md px-3 py-2 text-blue-900" data-testid={`update-note-${o.ticket_no}-${i}`}>
                            <span className="font-mono text-blue-500">{new Date(u.at).toLocaleString()}</span> — {u.text}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 md:min-w-[220px]">
                    <Select value={o.status} onValueChange={(v) => handleStatusSelect(o, v)}>
                      <SelectTrigger className="bg-white" data-testid={`status-select-${o.ticket_no}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="new" data-testid={`status-option-new-${o.ticket_no}`}>New</SelectItem>
                        <SelectItem value="in_progress" data-testid={`status-option-in_progress-${o.ticket_no}`}>In Progress</SelectItem>
                        <SelectItem value="on_hold" data-testid={`status-option-on_hold-${o.ticket_no}`}>On Hold</SelectItem>
                        <SelectItem value="completed" data-testid={`status-option-completed-${o.ticket_no}`}>Completed</SelectItem>
                        <SelectItem value="cancelled" data-testid={`status-option-cancelled-${o.ticket_no}`}>Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button onClick={() => requestUpdate(o)} variant="outline" className="bg-white" data-testid={`add-update-button-${o.ticket_no}`}>
                      <MessageSquarePlus className="w-4 h-4 mr-2" />Update
                    </Button>
                    <Button onClick={() => requestDueDate(o)} variant="outline" className="bg-white" data-testid={`edit-due-date-button-${o.ticket_no}`}>
                      <CalendarIcon className="w-4 h-4 mr-2" />Due Date
                    </Button>
                    <Button onClick={() => requestEdit(o)} variant="outline" className="bg-white" data-testid={`edit-work-order-button-${o.ticket_no}`}>
                      <Pencil className="w-4 h-4 mr-2" />Edit
                    </Button>
                    {o.status !== "completed" && (
                      <Button onClick={() => requestComplete(o)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`mark-complete-button-${o.ticket_no}`}>
                        <CheckCircle2 className="w-4 h-4 mr-2" />Mark Complete
                      </Button>
                    )}
                    <Button variant="ghost" onClick={() => removeOrder(o.id)} className="text-slate-500 hover:text-red-600" data-testid={`delete-button-${o.ticket_no}`}>
                      <Trash2 className="w-4 h-4 mr-2" />Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <WorkOrderDialogs
        api={api}
        completingOrder={completingOrder} setCompletingOrder={setCompletingOrder}
        completionNotes={completionNotes} setCompletionNotes={setCompletionNotes} confirmComplete={confirmComplete}
        holdingOrder={holdingOrder} setHoldingOrder={setHoldingOrder}
        holdNotes={holdNotes} setHoldNotes={setHoldNotes} confirmHold={confirmHold}
        progressOrder={progressOrder} setProgressOrder={setProgressOrder}
        progressPhotos={progressPhotos} setProgressPhotos={setProgressPhotos} confirmProgress={confirmProgress}
        updatingOrder={updatingOrder} setUpdatingOrder={setUpdatingOrder}
        updateNote={updateNote} setUpdateNote={setUpdateNote}
        updatePhotos={updatePhotos} setUpdatePhotos={setUpdatePhotos} confirmUpdate={confirmUpdate}
        dueDateOrder={dueDateOrder} setDueDateOrder={setDueDateOrder}
        dueDateValue={dueDateValue} setDueDateValue={setDueDateValue}
        dueDateNa={dueDateNa} setDueDateNa={setDueDateNa} confirmDueDate={confirmDueDate}
        editOrder={editOrder} setEditOrder={setEditOrder}
        editForm={editForm} setEditForm={setEditForm} confirmEdit={confirmEdit} meta={meta}
      />
    </div>
  );
}
